/**
 * A kitchen maker's world, as data.
 *
 * Onboarding a maker is writing one of these, not shipping a release. Everything the tool
 * shows -- what it looks like, what you can put in a run, what the doors are called, what the
 * factory will and will not cut -- comes from here. That is the whole multi-tenant model, and
 * keeping it to one plain object is what stops the second maker becoming a second branch.
 *
 * The dimensions below are the Australian standards, not invented ones: a 720 base carcass on
 * a 150 kick under a 33 benchtop, 560 deep; overheads 720 tall and 320 deep above a 600
 * splashback; widths on the familiar 150/300/450/600/750/900 ladder. A homeowner who has
 * measured a kitchen before will recognise every number here, and a maker will price from
 * them without a phone call.
 */

import { preset, presetRuns } from './room.js'

export const DEMO_MAKER = {
  id: 'harbourside',
  name: 'Harbourside Kitchens',

  /*
   * Four colours, and the whole app is theirs.
   *
   * Everything else -- the hairline between rows, the fill behind a swatch, the grey a
   * caption is set in -- is mixed out of `ink` and `paper` by `brand.js`, so onboarding a
   * maker is naming their ink and their paper rather than nominating a value for every
   * border in a stylesheet.
   */
  brand: {
    ink: '#1C1917',
    paper: '#FAF8F5',
    accent: '#9A6B3F',
    room: '#ECE7E0',
    warn: '#8A3E1E',
  },

  /**
   * What the factory will do. Enforced while a finger is moving, not checked at the end.
   */
  limits: {
    maxCabinetWidth: 1200,
    minFiller: 30,
    benchtopOverhang: 20,
    benchtopThickness: 33,
    kickHeight: 150,
    splashbackHeight: 600,
    baseHeight: 720,
    baseDepth: 560,
    wallHeight: 720,
    wallDepth: 320,
    /*
     * How far a corner unit reaches along BOTH walls.
     *
     * `b900c` is 900 wide and 900 deep into the corner -- an L, not a box. The run coming
     * into the corner starts past this rather than past the bare 560 overlap, which is what
     * makes room for the return leg to be drawn.
     *
     * Optional: a maker who does not stock a corner unit leaves it out and gets the plain
     * geometric allowance, which is what this app did before the corner was drawn honestly.
     */
    cornerReturn: 900,

    /*
     * And the same for the overheads, because a corner has a top half too.
     *
     * Without this the run coming into the corner stops at the bare 320 overlap and the
     * corner is filled with whatever sliver is left over -- which is how the starter kitchen
     * ended up with a 150mm strip of door board in the one place a person looks. `w900c` is
     * the blind corner wall unit that goes there. Optional, like `cornerReturn`: a maker who
     * does not stock one leaves it out and gets exactly the old behaviour.
     */
    wallCornerReturn: 900,
  },

  cabinets: [
    // Base. The ladder every Australian kitchen is built on.
    { id: 'b300', name: '300 base', kind: 'base', w: 300, h: 720, d: 560,
      doors: 1, drawers: 0, stretch: [280, 330], band: 'A' },
    { id: 'b450', name: '450 base', kind: 'base', w: 450, h: 720, d: 560,
      doors: 1, drawers: 0, stretch: [420, 480], band: 'A' },
    { id: 'b600', name: '600 base', kind: 'base', w: 600, h: 720, d: 560,
      doors: 2, drawers: 0, stretch: [560, 650], band: 'A' },
    { id: 'b450d', name: '450 drawers', kind: 'base', w: 450, h: 720, d: 560,
      doors: 0, drawers: 3, stretch: [420, 480], band: 'B' },
    { id: 'b600d', name: '600 drawers', kind: 'base', w: 600, h: 720, d: 560,
      doors: 0, drawers: 3, stretch: [560, 650], band: 'B' },
    { id: 'b600p', name: '600 pot drawers', kind: 'base', w: 600, h: 720, d: 560,
      doors: 0, drawers: 2, stretch: [560, 650], band: 'B' },
    { id: 'b900d', name: '900 drawers', kind: 'base', w: 900, h: 720, d: 560,
      doors: 0, drawers: 4, stretch: [850, 950], band: 'B' },
    { id: 'b900c', name: '900 corner', kind: 'base', w: 900, h: 720, d: 560,
      doors: 1, drawers: 0, stretch: [880, 920], role: 'corner', band: 'B' },
    { id: 'b900', name: '900 base', kind: 'base', w: 900, h: 720, d: 560,
      doors: 2, drawers: 0, stretch: [850, 950], band: 'A' },
    { id: 'b900s', name: '900 sink base', kind: 'base', w: 900, h: 720, d: 560,
      doors: 2, drawers: 0, stretch: [850, 950], role: 'sink', band: 'B' },
    { id: 'b750c', name: '750 cooktop base', kind: 'base', w: 750, h: 720, d: 560,
      doors: 0, drawers: 2, stretch: [700, 800], role: 'cooktop', band: 'B' },
    { id: 'b600dw', name: '600 dishwasher space', kind: 'base', w: 600, h: 720, d: 560,
      doors: 0, drawers: 0, stretch: [600, 600], role: 'dishwasher', band: 'A' },
    { id: 'b600b', name: '600 bin drawer', kind: 'base', w: 600, h: 720, d: 560,
      doors: 0, drawers: 1, stretch: [560, 650], band: 'B' },

    // Overhead.
    { id: 'w450', name: '450 overhead', kind: 'wall', w: 450, h: 720, d: 320,
      doors: 1, drawers: 0, stretch: [420, 480], band: 'A' },
    { id: 'w600s', name: '600 short overhead', kind: 'wall', w: 600, h: 450, d: 320,
      doors: 2, drawers: 0, stretch: [560, 650], band: 'A' },
    { id: 'w600t', name: '600 tall overhead', kind: 'wall', w: 600, h: 900, d: 320,
      doors: 2, drawers: 0, stretch: [560, 650], band: 'B' },
    { id: 'w600n', name: '600 shallow overhead', kind: 'wall', w: 600, h: 720, d: 220,
      doors: 2, drawers: 0, stretch: [560, 650], band: 'A' },
    { id: 'w600', name: '600 overhead', kind: 'wall', w: 600, h: 720, d: 320,
      doors: 2, drawers: 0, stretch: [560, 650], band: 'A' },
    { id: 'w900', name: '900 overhead', kind: 'wall', w: 900, h: 720, d: 320,
      doors: 2, drawers: 0, stretch: [850, 950], band: 'A' },
    { id: 'w600l', name: '600 lift-up', kind: 'wall', w: 600, h: 720, d: 320,
      doors: 1, drawers: 0, stretch: [560, 650], band: 'B' },
    { id: 'w900c', name: '900 corner overhead', kind: 'wall', w: 900, h: 720, d: 320,
      doors: 1, drawers: 0, stretch: [880, 920], role: 'corner', band: 'B' },
    { id: 'w900o', name: '900 open shelf', kind: 'wall', w: 900, h: 720, d: 320,
      doors: 0, drawers: 0, stretch: [850, 950], role: 'open', band: 'A' },
    { id: 'w900h', name: '900 rangehood', kind: 'wall', w: 900, h: 720, d: 320,
      doors: 0, drawers: 0, stretch: [600, 900], role: 'rangehood', band: 'B' },
    { id: 'w300', name: '300 overhead', kind: 'wall', w: 300, h: 720, d: 320,
      doors: 1, drawers: 0, stretch: [280, 330], band: 'A' },
    { id: 'w750', name: '750 overhead', kind: 'wall', w: 750, h: 720, d: 320,
      doors: 2, drawers: 0, stretch: [700, 800], band: 'A' },
    { id: 'w900t', name: '900 tall overhead', kind: 'wall', w: 900, h: 900, d: 320,
      doors: 2, drawers: 0, stretch: [850, 950], band: 'B' },

    { id: 'b400', name: '400 shallow base', kind: 'base', w: 400, h: 720, d: 400,
      doors: 1, drawers: 0, stretch: [350, 450], band: 'A' },
    { id: 'b750d', name: '750 drawers', kind: 'base', w: 750, h: 720, d: 560,
      doors: 0, drawers: 3, stretch: [700, 800], band: 'B' },
    { id: 'b800', name: '800 base', kind: 'base', w: 800, h: 720, d: 560,
      doors: 2, drawers: 0, stretch: [750, 850], band: 'A' },

    /*
     * TALL, and this is the half of a kitchen the first range did not have.
     *
     * They are in WIDTH order, which is how somebody reads them, and the generator no
     * longer depends on that: `design.js` used to take the first match for a role, so the
     * list order decided that a generated kitchen got a bar fridge or a family one. It
     * picks the space nearest the appliance's own size now (`appliance.js`'s SIZE), which
     * is the rule that was meant all along.
     *
     * The ladder is the one an Australian flat-pack range actually sells: pantries at 450,
     * 600 and 900 in two heights -- 2100 with a bulkhead over, 2300 to the ceiling -- fridge
     * spaces from 600 to 1000 because a fridge is bought before the kitchen is, and 580 deep
     * on a pantry against 560 on a base, which is real and is why a pantry door sits proud.
     *
     * The OVEN is one cabinet, not four. Single or double, and where the aperture sits, are
     * a finish (`maker.ovens`, and OVEN_AT in appliance.js puts it at waist height) rather
     * than a different carcass -- which is how it is sold and how it is installed.
     */
    { id: 't600f', name: 'Fridge space, 600', kind: 'tall', w: 600, h: 2100, d: 560,
      doors: 0, drawers: 0, stretch: [600, 650], role: 'fridge', band: 'A' },
    { id: 't750f', name: 'Fridge space, 750', kind: 'tall', w: 750, h: 2100, d: 560,
      doors: 0, drawers: 0, stretch: [750, 800], role: 'fridge', band: 'A' },
    { id: 't900f', name: 'Fridge space, 900', kind: 'tall', w: 900, h: 2100, d: 560,
      doors: 0, drawers: 0, stretch: [900, 950], role: 'fridge', band: 'A' },
    { id: 't1000f', name: 'Fridge space, 1000', kind: 'tall', w: 1000, h: 2100, d: 560,
      doors: 0, drawers: 0, stretch: [1000, 1100], role: 'fridge', band: 'A' },

    { id: 't600o', name: '600 oven tower', kind: 'tall', w: 600, h: 2100, d: 560,
      doors: 1, drawers: 0, stretch: [590, 610], role: 'oven', band: 'C' },

    { id: 't450p', name: '450 pantry', kind: 'tall', w: 450, h: 2100, d: 580,
      doors: 1, drawers: 0, stretch: [420, 480], band: 'C' },
    { id: 't600p', name: '600 pantry', kind: 'tall', w: 600, h: 2100, d: 580,
      doors: 2, drawers: 0, stretch: [560, 650], band: 'C' },
    { id: 't900p', name: '900 pantry', kind: 'tall', w: 900, h: 2100, d: 580,
      doors: 2, drawers: 0, stretch: [850, 950], band: 'C' },
    { id: 't450h', name: '450 pantry, to ceiling', kind: 'tall', w: 450, h: 2300, d: 580,
      doors: 1, drawers: 0, stretch: [420, 480], band: 'C' },
    { id: 't600h', name: '600 pantry, to ceiling', kind: 'tall', w: 600, h: 2300, d: 580,
      doors: 2, drawers: 0, stretch: [560, 650], band: 'C' },
    { id: 't900h', name: '900 pantry, to ceiling', kind: 'tall', w: 900, h: 2300, d: 580,
      doors: 2, drawers: 0, stretch: [850, 950], band: 'C' },

    { id: 't450b', name: '450 broom cupboard', kind: 'tall', w: 450, h: 2100, d: 560,
      doors: 1, drawers: 0, stretch: [420, 480], band: 'C' },
    { id: 't600b', name: '600 broom cupboard', kind: 'tall', w: 600, h: 2100, d: 560,
      doors: 1, drawers: 0, stretch: [560, 650], band: 'C' },
    { id: 't600c', name: '600 full-height cupboard', kind: 'tall', w: 600, h: 2300, d: 560,
      doors: 2, drawers: 0, stretch: [560, 650], band: 'C' },
    { id: 't900c', name: '900 full-height cupboard', kind: 'tall', w: 900, h: 2300, d: 560,
      doors: 2, drawers: 0, stretch: [850, 950], band: 'C' },
  ],

  /*
   * STYLE AND COLOUR ARE TWO CHOICES, NOT ONE.
   *
   * This started as a single list of eleven finished doors -- "Hampton Shaker, Sage" -- which
   * is how a catalogue photograph is captioned and not how anybody shops. Kaboodle and
   * Kinsman both sell it the way it is actually made: pick a profile, then pick a colour, and
   * the factory routs the one in the other. Three styles and eight colours is twenty-four
   * kitchens from eleven lines of data, and more importantly it is twenty-four DECISIONS a
   * homeowner can make instead of one they have to find in a list.
   */
  doorStyles: [
    { id: 'slab', name: 'Milano', style: 'slab',
      detail: 'A flat door with a square edge. The quiet one.' },
    { id: 'shaker', name: 'Hampton', style: 'shaker',
      detail: 'A framed door with a recessed centre.' },
    { id: 'vgroove', name: 'Coastal', style: 'vgroove',
      detail: 'Vertical grooves, for a country or coastal kitchen.' },
  ],

  /**
   * The colours, and `gloss` is the field that does the most work.
   *
   * A matt laminate and a two-pack gloss are the same shape and a completely different
   * kitchen, and it is the thing a homeowner is really choosing between.
   */
  colours: [
    { id: 'chalk', name: 'Chalk', colour: '#EFEAE1', gloss: 0.10 },
    { id: 'snow', name: 'Snow Gloss', colour: '#F7F6F3', gloss: 0.80 },
    { id: 'oyster', name: 'Oyster', colour: '#D9D0C3', gloss: 0.12 },
    { id: 'sage', name: 'Sage', colour: '#93A08C', gloss: 0.12 },
    { id: 'juniper', name: 'Juniper', colour: '#4C6152', gloss: 0.14 },
    { id: 'navy', name: 'Ink Navy', colour: '#31435A', gloss: 0.16 },
    { id: 'graphite', name: 'Graphite Gloss', colour: '#3A3A3C', gloss: 0.74 },
    // `grain` says this one is timber rather than a painted board, and texture.js draws it
    // as timber. Without it the oak door is a beige rectangle called oak.
    { id: 'oak', name: 'Natural Oak', colour: '#C4A176', gloss: 0.20, grain: true },
    // The second half of the palette. Four more matt colours a kitchen is actually
    // ordered in, two more glosses, and a second timber -- because one timber is a
    // sample, not a choice.
    { id: 'linen', name: 'Linen', colour: '#E3DBCD', gloss: 0.10 },
    { id: 'putty', name: 'Putty', colour: '#C6BCAD', gloss: 0.12 },
    { id: 'dove', name: 'Dove Grey', colour: '#B5B3AE', gloss: 0.12 },
    { id: 'olive', name: 'Olive', colour: '#6C7355', gloss: 0.13 },
    { id: 'forest', name: 'Forest', colour: '#2E4437', gloss: 0.14 },
    { id: 'terracotta', name: 'Terracotta', colour: '#AE7658', gloss: 0.12 },
    { id: 'midnight', name: 'Midnight Gloss', colour: '#1E2A38', gloss: 0.78 },
    { id: 'porcelain', name: 'Porcelain Gloss', colour: '#EDEDEA', gloss: 0.82 },
    { id: 'walnut', name: 'Walnut', colour: '#7C5B41', gloss: 0.22, grain: true },
  ],

  /** What goes behind the benchtop. A whole wall of colour, and it costs one plane to draw. */
  /*
   * What goes behind the benchtop.
   *
   * `tile` is the tile size in millimetres and it is what makes a tiled splashback look
   * tiled: the joints are drawn at their real size, so a 300 subway and a 600 square are
   * visibly different things. A glass or stone splashback is one sheet and has no tile
   * field, because drawing joints into it would be a lie about what is being supplied.
   */
  splashbacks: [
    { id: 'match', name: 'Match the benchtop', colour: null, gloss: 0.5 },
    { id: 'white', name: 'White gloss tile', colour: '#F4F3F0', gloss: 0.7,
      tile: [300, 100], grout: '#DCD8D1', joint: 3 },
    { id: 'sage', name: 'Sage glass', colour: '#8E9C88', gloss: 0.85 },
    { id: 'ink', name: 'Ink glass', colour: '#25313F', gloss: 0.85 },
    { id: 'terracotta', name: 'Terracotta tile', colour: '#B0705A', gloss: 0.35,
      tile: [200, 200], grout: '#C9BCAF', joint: 4 },
    { id: 'white-matt', name: 'White matt subway', colour: '#EDEAE4', gloss: 0.18,
      tile: [300, 100], grout: '#D6D1C9', joint: 3 },
    { id: 'charcoal-subway', name: 'Charcoal subway', colour: '#3B3B3D', gloss: 0.55,
      tile: [300, 100], grout: '#4E4E50', joint: 3 },
    { id: 'sage-tile', name: 'Sage square tile', colour: '#93A08C', gloss: 0.45,
      tile: [200, 200], grout: '#AFB7A9', joint: 4 },
    { id: 'blush', name: 'Blush glass', colour: '#D8BFB2', gloss: 0.85 },
  ],

  // `veined` is marble, `timber` is a solid timber top; both change how texture.js draws it.
  benchtops: [
    { id: 'carrara', name: 'Carrara stone', colour: '#F2F0EC', fleck: '#C9C5BE', gloss: 0.55, veined: true },
    { id: 'basalt', name: 'Basalt stone', colour: '#4A4A4C', fleck: '#6E6E70', gloss: 0.5 },
    { id: 'oak', name: 'Solid oak', colour: '#B98F5F', fleck: '#9C7444', gloss: 0.28, timber: true },
    { id: 'calacatta', name: 'Calacatta stone', colour: '#F6F4F0', fleck: '#B9B2A6', gloss: 0.6, veined: true },
    { id: 'concrete', name: 'Honed concrete', colour: '#9E9C98', fleck: '#84827E', gloss: 0.22 },
    { id: 'ink', name: 'Ink stone', colour: '#26282B', fleck: '#43464A', gloss: 0.58 },
    { id: 'walnut', name: 'Solid walnut', colour: '#7B5738', fleck: '#5D3F26', gloss: 0.3, timber: true },
  ],

  /*
   * THE ROOM AROUND THE KITCHEN, and it is not part of the quote.
   *
   * A cabinet maker does not supply the floor or paint the walls, so nothing here appears in
   * the schedule, the estimate or the drawings. It is here because a kitchen shown standing
   * on beige nothing against white nothing does not look like a kitchen, and the choice
   * somebody makes about their floor changes which doors they want. Priced at zero and said
   * so in the picker.
   *
   * `kind` decides how texture.js draws it: boards, tile or concrete. Sizes in millimetres.
   */
  floors: [
    { id: 'oak', name: 'Oak boards', kind: 'boards', colour: '#C9A87C', grain: '#A17F52',
      plank: [180, 1200], gloss: 0.24 },
    { id: 'blackbutt', name: 'Blackbutt boards', kind: 'boards', colour: '#B58C5C', grain: '#87613A',
      plank: [136, 1800], gloss: 0.26 },
    { id: 'limed', name: 'Limed oak boards', kind: 'boards', colour: '#D7CCBB', grain: '#B4A692',
      plank: [190, 1200], gloss: 0.16 },
    { id: 'walnut', name: 'Walnut boards', kind: 'boards', colour: '#7C5B41', grain: '#583C29',
      plank: [160, 1500], gloss: 0.28 },
    { id: 'stone-tile', name: 'Stone tile', kind: 'tile', colour: '#C3BAAC', grout: '#A0968A',
      tile: [600, 600], joint: 4, gloss: 0.20 },
    { id: 'charcoal-tile', name: 'Charcoal tile', kind: 'tile', colour: '#5A5A5C', grout: '#48484A',
      tile: [450, 450], joint: 4, gloss: 0.24 },
    { id: 'concrete', name: 'Polished concrete', kind: 'concrete', colour: '#B7B3AC', gloss: 0.34 },
  ],

  /*
   * THE FITTINGS, which are a style choice and not a different cabinet.
   *
   * A rangehood, a dishwasher and an oven all go in the same hole in the same run whichever
   * one you buy -- the range carries the SPACE (`w900h`, `b600dw`, `t600o`) and these say
   * what stands in it. Which is why they are finishes, chosen once for the kitchen, rather
   * than three more lines in `cabinets`: a person picks "integrated dishwasher" the way they
   * pick a handle, and the layout does not change when they do.
   *
   * The one that DOES change a size is the double oven, and it changes it inside the tower:
   * a taller aperture and a shorter cupboard above it. See appliance.js.
   */
  rangehoods: [
    { id: 'canopy', name: 'Canopy, stainless', style: 'canopy',
      detail: 'The chimney kind, on show above the cooktop.' },
    { id: 'under', name: 'Undermount, with a cupboard over', style: 'under',
      detail: 'A slim hood under a cupboard, so the wall keeps its storage.' },
    { id: 'concealed', name: 'Concealed behind a door', style: 'concealed',
      detail: 'Hidden in the cabinetry. Only the shadow line shows.' },
  ],

  fridges: [
    { id: 'freestanding', name: 'Freestanding, stainless', style: 'freestanding',
      detail: 'The fridge on show, 700 deep, standing proud of the bench.' },
    { id: 'integrated', name: 'Built in behind a door', style: 'integrated',
      detail: 'A cabinet door on the front, set back so it lines up with the run.' },
  ],

  dishwashers: [
    { id: 'integrated', name: 'Fully integrated', style: 'integrated',
      detail: 'A cabinet door on the front. The controls are on the top edge.' },
    { id: 'semi', name: 'Semi-integrated', style: 'semi',
      detail: 'A cabinet door with the control panel showing above it.' },
    { id: 'freestanding', name: 'Stainless front', style: 'freestanding',
      detail: 'The machine on show, in stainless.' },
  ],

  /*
   * `stack` is the apertures, bottom to top, in millimetres. A count would not do: "one and
   * a half" is a full 600 oven with a compact one over it, and a compact oven is 450 high,
   * not half of 595. The list is the shape of the hole, which is the thing the cabinet and
   * the drawing both need.
   */
  ovens: [
    { id: 'single', name: 'Single oven', stack: [595],
      detail: 'One 600 oven, mounted at waist height.' },
    { id: 'oneandhalf', name: 'Oven and compact oven', stack: [595, 450],
      detail: 'A full oven with a compact oven or a combi microwave over it.' },
    { id: 'double', name: 'Double oven', stack: [595, 595],
      detail: 'Two full ovens stacked in the same tower. The cupboard above gets shorter.' },
  ],

  /** Wall paint. Same reasoning as the floor, and the same zero on the quote. */
  wallColours: [
    { id: 'white', name: 'Natural white', colour: '#F3EFE9' },
    { id: 'linen', name: 'Linen', colour: '#E8E1D5' },
    { id: 'clay', name: 'Soft clay', colour: '#DCCEC0' },
    { id: 'sage', name: 'Pale sage', colour: '#D4D9CE' },
    { id: 'mist', name: 'Blue mist', colour: '#D2DAE1' },
    { id: 'stone', name: 'Warm stone', colour: '#C6BEB2' },
    { id: 'charcoal', name: 'Charcoal', colour: '#4B4B4D' },
  ],

  handles: [
    { id: 'bar-black', name: 'Bar, matt black', style: 'bar', colour: '#1A1A1A' },
    { id: 'bar-brass', name: 'Bar, aged brass', style: 'bar', colour: '#A9834B' },
    { id: 'd-steel', name: 'D-pull, stainless', style: 'd', colour: '#B7BABD' },
    { id: 'bar-steel', name: 'Bar, stainless', style: 'bar', colour: '#B7BABD' },
    { id: 'knob-black', name: 'Knob, matt black', style: 'knob', colour: '#1A1A1A' },
    { id: 'knob-brass', name: 'Knob, aged brass', style: 'knob', colour: '#A9834B' },
    { id: 'd-black', name: 'D-pull, matt black', style: 'd', colour: '#1A1A1A' },
    { id: 'none', name: 'Handleless', style: 'none', colour: '#000000' },
  ],

  /**
   * What it costs, as bands.
   *
   * Every cabinet carries a band; this says what a band is worth, supplied and fitted, in
   * this maker's dollars. The profile and a gloss finish multiply what has doors on it; stone
   * is per metre of base run; handles per front. A maker who leaves this block out gets no
   * estimate shown anywhere, which is better than a guess under their name. See price.js.
   */
  pricing: {
    currency: 'AUD',
    bands: { A: 520, B: 780, C: 1450 },
    filler: 60,
    doorStyles: { slab: 1, shaker: 1.18, vgroove: 1.12 },
    glossFactor: 1.22,
    // Handleless is the dearest: every door is routed for a finger pull.
    handles: { 'bar-black': 14, 'bar-brass': 22, 'd-steel': 12, 'bar-steel': 16,
      'knob-black': 9, 'knob-brass': 14, 'd-black': 12, none: 35 },
    // per metre, supplied and fitted
    benchtop: { carrara: 780, basalt: 820, oak: 480, calacatta: 940, concrete: 690, ink: 860, walnut: 560 },
    // per metre; 'match' is half the stone rate
    splashback: { white: 240, sage: 520, ink: 520, terracotta: 310,
      'white-matt': 220, 'charcoal-subway': 280, 'sage-tile': 300, blush: 520 },
    installRate: 0.12,
    spread: 0.08,
  },
}

/** A starter kitchen, so nobody meets an empty room. */
export function starterPlan(maker) {
  return {
    id: 'demo',
    makerId: maker.id,
    roomHeight: 2400,
    shape: 'l',
    /*
     * The room is a loop of walls, in millimetres, clockwise on the plan. Presets are all
     * boxes; what makes an L an L is which walls carry runs. See room.js.
     */
    walls: preset('l', { width: 4200, depth: 3600 }),
    doorStyleId: 'shaker',
    colourId: 'sage',
    handleId: 'bar-brass',
    benchtopId: 'basalt',
    splashbackId: 'white',
    kickColourId: 'graphite',
    floorId: 'oak',
    wallColourId: 'white',
    rangehoodId: 'canopy',
    fridgeId: 'freestanding',
    dishwasherId: 'integrated',
    ovenId: 'single',
    /*
     * Runs attach to walls by id. Where two runs of a kind meet at a corner, the second
     * around the loop gives way by the corner allowance (room.extents), so the two never
     * stand on the same floor -- and the corner is left for a corner cabinet.
     */
    runs: presetRuns('l').map(([wallId, kind], i) => ({ id: 'r' + (i + 1), wallId, kind, items: [] })),
  }
}
